import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BTN_DANGER, BTN_PRIMARY, BTN_QUIET, Confirm, FIELD, Modal, fail, useGuard } from "./dialogs";
import { FLAG_LABEL, Pill, show, useLoad, when } from "./ui";

// Everything about one person, and everything the admin may do to their account.
// Counts, dates, states and fixed categories only. Nothing they wrote is shown here.

type Person = any;

const KINDS: Record<string, string> = {
  send_email: "Send an email", send_text_to_someone: "Text someone for them",
  phone_call: "Make a phone call", calendar_change: "Change a calendar",
  photo_or_file: "A photo or file", web_lookup: "Look something up online",
  booking_or_reservation: "Book or reserve", purchase_or_payment: "Buy or pay",
  directions_or_travel: "Directions or travel", shared_with_family: "Share with family",
  smart_home_or_device: "Control a device", background_watching: "Keep watch on something",
  repeating_schedule: "A repeating schedule", lists_or_notes: "Lists or notes",
  other: "Something else",
};

const PAUSE_WHY: [string, string][] = [
  ["admin", "No particular reason"],
  ["number_compromised", "Someone else may have their number"],
  ["abuse", "Misuse"],
  ["cost", "Running up cost"],
];
const CHOICE = "mt-2 flex min-h-[44px] items-center gap-3 rounded-2xl bg-secondary/40 px-4 py-2 text-sm";
const RADIO = "h-5 w-5 shrink-0 accent-[hsl(var(--primary))]";

function Stat({ label, value }: { label: string; value: any }) {
  return (
    <div className="rounded-xl bg-secondary/40 px-3 py-2">
      <div className="text-base font-extrabold tabular-nums">{show(value)}</div>
      <div className="text-[11px] leading-tight text-muted-foreground">{label}</div>
    </div>
  );
}

function Heading({ children }: { children: any }) {
  return <div className="mt-5 text-xs font-extrabold uppercase tracking-[0.14em] text-muted-foreground">{children}</div>;
}

// A week still in progress says so, so an early "2 days" is not read as a bad week.
function week(p: Person, n: number): string | null {
  const days = p[`week${n}_days`];
  if (days == null) return null;
  const d = `${days} day${days === 1 ? "" : "s"}`;
  return p.full_weeks != null && p.full_weeks < n ? `${d} so far` : `${days} of 7 days`;
}

export function lastSeen(p: Person): string {
  if (p.days_since_active == null) return "No texts in the last 90 days";
  if (p.days_since_active === 0) return "Texted today";
  if (p.days_since_active === 1) return "Texted yesterday";
  return `Last texted ${p.days_since_active} days ago`;
}

export default function PersonSheet({ person: p, onClose, onChanged }: {
  person: Person | null; onClose: () => void; onChanged: () => void;
}) {
  const guard = useGuard();
  const [box, setBox] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [why, setWhy] = useState("admin");
  const [rejoin, setRejoin] = useState("block");
  const [code, setCode] = useState<any>(null);
  const [tierBusy, setTierBusy] = useState(false);
  const id = p?.user_id;
  useEffect(() => { setBox(null); setCode(null); setName(p?.name || ""); setNumber(""); setWhy("admin"); setRejoin("block"); }, [id]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!p) return null;
  const first = (p.name || "").split(" ")[0] || "them";
  const can = (a: string) => (p.actions || []).includes(a);
  const post = (path: string, body?: any) => guard(() => api.post(`/admin/users/${id}/${path}`, body));
  const after = (message: string) => { toast.success(message); onChanged(); };

  return (
    <Modal open onClose={onClose} title={p.name} tall
      note={[p.phone, lastSeen(p), p.is_admin ? "admin" : null].filter(Boolean).join(" · ")}>
      <div data-testid={`person-sheet-${id}`}>
        <div className="flex flex-wrap gap-1.5">
          {p.state === "active" && (p.flags || []).length === 0 && <Pill tone="good">Nothing wrong</Pill>}
          {(p.flags || []).map((f: string) => (
            <Pill key={f} tone={FLAG_LABEL[f]?.tone}>{FLAG_LABEL[f]?.text || f}</Pill>
          ))}
        </div>

        {(p.silent_because || []).length > 0 && (
          <div className="mt-4 rounded-2xl border border-border bg-card p-4" data-testid="person-silent">
            <div className="text-sm font-extrabold">Why Remy may be quiet for {first}</div>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
              {p.silent_because.map((s: any) => <li key={s.flag}>{s.why}</li>)}
            </ul>
          </div>
        )}

        <p className="mt-4 text-sm text-muted-foreground">
          Joined {p.joined || "—"} by {p.joined_by === "sms" ? "text" : "website"}
          {p.google_status === "connected" ? " · Google connected"
            : p.google_status === "needs_reauth" ? " · Google needs reconnecting"
            : p.google_status === "disconnected" ? " · Google not connected" : ""}
          {p.linked_with ? ` · linked with ${p.linked_with} ${p.linked_with === 1 ? "person" : "people"}` : ""}
          {p.tier === "tester" ? " · higher daily limits" : ""}
        </p>

        <Heading>Coming back</Heading>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="First week" value={week(p, 1)} />
          <Stat label="Second week" value={week(p, 2)} />
          <Stat label="Fourth week" value={week(p, 4)} />
          <Stat label="Days active, last 30" value={p.active_days_30} />
        </div>

        <Heading>Following through, last 30 days</Heading>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Texts to Remy" value={p.texts_in_30} />
          <Stat label="Reminders and heads-ups sent" value={p.reminders_sent_30} />
          <Stat label="Answered within 2 hours"
            value={p.reminders_sent_30 ? `${p.reminders_answered_30} (${p.reminders_answered_pct}%)` : null} />
          <Stat label="Reminders that failed, 7 days" value={p.reminders_failed_7} />
          <Stat label="Plans sent to others" value={p.plans_sent_30} />
          <Stat label="Plans accepted" value={p.plans_accepted_30} />
          <Stat label="Replies that failed" value={p.replies_failed_30} />
          <Stat label="AI cost" value={p.cost_30 == null ? null : `$${Number(p.cost_30).toFixed(2)}`} />
        </div>

        <Heading>Asked for, Remy couldn't do ({show(p.unmet_30)})</Heading>
        {Object.keys(p.unmet_kinds || {}).length > 0 ? (
          <ul className="mt-2 list-disc pl-5 text-sm">
            {Object.entries(p.unmet_kinds).map(([k, n]) => <li key={k}>{KINDS[k] || k}: {String(n)}</li>)}
          </ul>
        ) : <p className="mt-2 text-sm text-muted-foreground">Nothing in the last 30 days.</p>}
        <p className="mt-1 text-[11px] text-muted-foreground">
          The kind of thing only. Days are counted in UTC, so an evening text can show under the next day.
        </p>

        <History id={id} />

        {(p.actions || []).length > 0 && <Heading>Change this account</Heading>}
        <div className="mt-2 flex flex-wrap gap-2">
          {can("rename") && <button type="button" className={BTN_QUIET} onClick={() => { setName(p.name || ""); setBox("rename"); }} data-testid="act-rename">Fix name</button>}
          {can("move_number") && <button type="button" className={BTN_QUIET} onClick={() => setBox("move")} data-testid="act-move">New phone number</button>}
          {can("set_tier") && (
            <button type="button" className={BTN_QUIET} data-testid={`admin-tier-${id}`} disabled={tierBusy}
              onClick={async () => {
                const tier = p.tier === "tester" ? "standard" : "tester";
                setTierBusy(true);
                try {
                  await api.post(`/admin/users/${id}/tier`, { tier });
                  after(tier === "tester" ? "Higher daily limits turned on" : "Back to standard limits");
                } catch (e) { fail(e); } finally { setTierBusy(false); }
              }}>
              {p.tier === "tester" ? "Higher daily limits: on" : "Higher daily limits: off"}
            </button>
          )}
          {can("stop_texts") && <button type="button" className={BTN_QUIET} onClick={() => setBox("stop")} data-testid="act-stop">Stop texts to {first}</button>}
          {can("disconnect_google") && <button type="button" className={BTN_QUIET} onClick={() => setBox("google")} data-testid="act-google">Disconnect Google</button>}
          {can("pause") && <button type="button" className={BTN_QUIET} onClick={() => setBox("pause")} data-testid="act-pause">Pause Remy for {first}</button>}
          {can("resume") && <button type="button" className={BTN_PRIMARY} onClick={() => setBox("resume")} data-testid="act-resume">Resume Remy for {first}</button>}
        </div>
        {can("delete") && (
          <div className="mt-6 border-t border-border pt-4">
            <button type="button" className={BTN_DANGER} onClick={() => setBox("delete")} data-testid="act-delete">
              Delete this account
            </button>
          </div>
        )}
      </div>

      <Confirm open={box === "rename"} onClose={() => setBox(null)} title="Fix name" action="Save"
        ok={/[A-Za-z\u00C0-\uFFFF]/.test(name)}
        body={`This is the name Remy uses for ${first}, and the name people linked with them see.`}
        run={async () => { await post("name", { name }); after("Name saved"); }}>
        <input value={name} onChange={(e) => setName(e.target.value)} className={FIELD}
          aria-label="Name" data-testid="rename-input" />
      </Confirm>

      <Confirm open={box === "move" && !code} onClose={() => setBox((b) => (b === "move" ? null : b))} title="New phone number" action="Get the code"
        ok={number.replace(/[^0-9]/g, "").length >= 10}
        body={`For when ${first} has lost their old phone. Only do this if you are sure it is really them. Nothing moves until they text a code to Remy from the new phone and reply YES.`}
        run={async () => {
          const r: any = await post("move-number", { mobile_number: number });
          setCode(r.data); setBox("code");
        }}>
        <input value={number} onChange={(e) => setNumber(e.target.value)} inputMode="tel"
          placeholder="New mobile number" aria-label="New mobile number" className={FIELD}
          data-testid="move-input" />
      </Confirm>

      <Modal open={box === "code" && !!code} onClose={() => { setBox(null); setCode(null); }} title="Give them this code"
        note={code?.message}>
        <div className="rounded-2xl bg-secondary/40 px-4 py-5 text-center font-display text-3xl tracking-[0.2em]"
          data-testid="move-code">
          {code ? `${code.code.slice(0, 3)} ${code.code.slice(3)}` : ""}
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" className={BTN_QUIET}
            onClick={async () => {
              try { await navigator.clipboard.writeText(code.code); toast.success("Code copied"); }
              catch { toast.message(code.code); }
            }}>Copy code</button>
          <button type="button" className={BTN_PRIMARY} onClick={() => { setBox(null); setCode(null); onChanged(); }}>Done</button>
        </div>
      </Modal>

      <Confirm open={box === "stop"} onClose={() => setBox(null)} title={`Stop texts to ${first}?`} action="Stop texts"
        body={`Remy will stop texting ${first}. Only ${first} can turn texts back on, by texting START. Use this when they asked you by phone or email.`}
        run={async () => { await post("stop-texts"); after("Texts stopped"); }} />

      <Confirm open={box === "google"} onClose={() => setBox(null)} title="Disconnect Google?" action="Disconnect"
        body={`Remy loses access to ${first}'s Google and stops checking their email and calendar. ${first} gets one text saying so and can reconnect any time.`}
        run={async () => { await post("disconnect-google"); after("Google disconnected"); }} />

      <Confirm open={box === "pause"} onClose={() => setBox(null)} title={`Pause Remy for ${first}?`} action="Pause"
        body="Remy stops replying and stops all reminders. Nothing is deleted. Reminders that come due while paused are not sent later. STOP and HELP still work."
        run={async () => { await post("pause", { reason: why }); after("Paused"); }}>
        <fieldset data-testid="pause-why">
          <legend className="text-sm font-semibold">Why</legend>
          {PAUSE_WHY.map(([k, label]) => (
            <label key={k} className={CHOICE}>
              <input type="radio" name="pause-why" value={k} checked={why === k} onChange={() => setWhy(k)} className={RADIO} />
              {label}
            </label>
          ))}
          <p className="mt-2 text-xs text-muted-foreground">
            A paused person who texts Remy is told once a day that Remy is paused. If you pick
            "Someone else may have their number", they are told nothing.
          </p>
        </fieldset>
      </Confirm>

      <Confirm open={box === "resume"} onClose={() => setBox(null)} title={`Resume Remy for ${first}?`} action="Resume"
        body="Remy starts replying and sending reminders again. If they texted STOP, texts stay off until they text START."
        run={async () => { await post("resume"); after("Resumed"); }} />

      <Confirm open={box === "delete"} onClose={() => setBox(null)} title="Delete this account?" action="Delete forever" danger
        typed="DELETE"
        body="This permanently deletes everything Remy holds for this person: messages, reminders, lists, memories, links and their Google connection. It cannot be undone. It usually finishes within a few minutes."
        run={async () => { await post("delete", { confirm_name: p.name, rejoin }); toast.success("Deleting. It will be gone in a few minutes."); onChanged(); onClose(); }}>
        <div className="rounded-2xl border border-destructive/40 bg-destructive/10 px-4 py-3" data-testid="delete-who">
          <div className="text-base font-extrabold">{p.name}</div>
          <div className="text-sm text-muted-foreground">{p.phone || "No phone number"} · {lastSeen(p)}</div>
        </div>
        <fieldset>
          <legend className="text-sm font-semibold">Can {first} come back later?</legend>
          {([["block", "No, not unless I invite them again"], ["allow", "Yes, they can text Remy to start again"],
            ["remove", "Remove the invitation too (a test account)"]] as const).map(([k, label]) => (
            <label key={k} className={CHOICE}>
              <input type="radio" name="rejoin" value={k} checked={rejoin === k} onChange={() => setRejoin(k)} className={RADIO} />
              {label}
            </label>
          ))}
        </fieldset>
      </Confirm>
    </Modal>
  );
}

/** What happened on the account lately and what the admin changed: the kind and the time. */
function History({ id }: { id: string }) {
  const events = useLoad<any[]>(`/admin/people/${id}/events`);
  const changes = useLoad<any[]>(`/admin/log?user_id=${id}`);
  const rows = (list: any[] | null, empty: string) => (list || []).length === 0
    ? <p className="mt-2 text-sm text-muted-foreground">{empty}</p>
    : (
      <div className="mt-2 overflow-hidden rounded-2xl border border-border bg-card">
        {(list || []).slice(0, 8).map((e, i) => (
          <div key={i} className={`flex min-h-[40px] items-center justify-between gap-3 px-4 py-2 text-sm ${i ? "border-t border-border" : ""}`}>
            <span>{e.what}</span>
            <span className="shrink-0 text-xs text-muted-foreground">{when(e.at)}</span>
          </div>
        ))}
      </div>
    );
  return (
    <>
      <Heading>What you changed</Heading>
      {changes.loading && !changes.data ? <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
        : rows(changes.data, "You haven't changed anything on this account.")}
      <Heading>Recent activity (kind and time only)</Heading>
      {events.loading && !events.data ? <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
        : rows(events.data, "Nothing recorded yet.")}
    </>
  );
}
